import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import type { EmployeeItem, RoleItem } from "../../../types/employee";
import type { Store } from "../../../types/store";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { v4 as uuid } from "uuid";
import { createEmployee, getEmployees, optimisticAddEmployee, updateEmployee } from "../../../slices/employeeSlice";
import { toast } from "react-toastify";
import { EditIcon, XCircle } from "lucide-react";
import UpdateEmployeeBasicInfoModal from "../UpdateEmployeeBasicInfoModal";
import UpdateRolesAndStoresModal from "../UpdateEmployeeRoleInfoModal";


interface Props {
  open: boolean;
  initial?: EmployeeItem | null;
  stores: Store[];
  roles: RoleItem[];
  onClose: () => void;
  onSave: (emp: EmployeeItem) => void;
}

const EmployeeSchema = Yup.object().shape({
  firstname: Yup.string().required("First name required"),
  lastname: Yup.string().required("Last name is required"),
  othername: Yup.string().optional(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: Yup.string().optional(),
  roleIds: Yup.array().of(Yup.string()),
  stores: Yup.array().of(Yup.string()).min(1, "Assign at least one store"),
});




const AddEditEmployeeModal: React.FC<Props> = ({ open, initial, stores, roles, onClose, onSave }) => {
  if (!open) return null;

  console.log("Initial: ", initial);

  const dispatch = useAppDispatch();
  const adding = useAppSelector((state) => state.employees.adding);

  const [openEditBasicInfoModal, setOpenEditBasicInfoModal] = useState(false);
  const [openEditRoleInfoModal, setOpenEditRoleInfoModal] = useState(false);

  const handleSubmit = async (values: any, { resetForm }: any) => {

    console.log("Details: ", values);
    // 1️⃣ Create temporary employee for optimistic UI
    const tempEmployee = {
      id: uuid(), // generate temporary ID
      firstname: values.firstname,
      lastname: values.lastname,
      username: `${values.firstname[0]}${values.lastname}`.toLowerCase(),
      email: values.email,
      phoneNumber: values.phoneNumber,
      roleIds: [],           // or connect roles if needed
      stores: [],          // or connect stores if needed
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // 2️⃣ Optimistically add to UI
    dispatch(optimisticAddEmployee(tempEmployee));

    // 3️⃣ Trigger backend call
    const result = await dispatch(createEmployee(values));
    onSave((result.payload as any).data);
    onClose();

    if (createEmployee.fulfilled.match(result)) {
      dispatch(getEmployees());
      resetForm();
      toast.success("Employee created");
    } else {
      toast.error(result.payload as string);
    }
  };

  const defaultValues: EmployeeItem = {
    id: "",
    firstname: "",
    lastname: "",
    othername: "",
    username: "",
    email: "",
    phoneNumber: "",
    mainRole: null,
    roleIds: [],
    stores: [],
    assignedStores: [],
    assignedRoles: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/40 p-4 z-[999]"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-8 max-h-screen overflow-y-scroll"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-lg text-left font-bold mb-4">
                {initial ? "Employee Profile" : "Add Employee"}
              </h3>
            </div>
            <div>
              <XCircle
                onClick={onClose}
                color="red"
                className="hover:cursor-pointer"
              />
            </div>
          </div>

          <Formik
            initialValues={defaultValues}
            validationSchema={EmployeeSchema}
            onSubmit={async (values, formikHelpers) => {
              // If we are CREATING a new employee
              if (!initial) {
                await handleSubmit(values, formikHelpers); // calls optimistic create logic
                onClose();
                return;
              }

              // ✅ EDIT employee
              const payload: EmployeeItem = {
                ...values,
                id: values.id,
                updatedAt: new Date().toISOString(),
                createdAt: values.createdAt || new Date().toISOString(),
              };

              try {
                // Dispatch RTK employee update
                const result = await dispatch(updateEmployee({ id: payload.id, data: payload }));

                if (updateEmployee.fulfilled.match(result)) {
                  toast.success("Employee updated successfully");
                  onClose(); // close only on success
                } else {
                  toast.error(result.payload as string);
                }
              } catch (err) {
                toast.error("Failed to update employee");
              }
            }}

          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className="grid gap-3">

                {/* BASIC INFORMATION */}
                {initial ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-700">Basic Information</h4>
                      <button
                        type="button"
                        className="modal-btn-primary"
                        onClick={() => {
                          setOpenEditBasicInfoModal(true)
                        }}
                      >
                        <EditIcon />
                        Edit
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-3">
                      <div className="form-label">
                        <p className="form-label">Firstname</p>
                        <p className="font-medium form-input">{initial.firstname}</p>
                      </div>
                      <div className="form-label">
                        <p className="form-label">Othername {initial.othername?.length && 's'}</p>
                        <p className="font-medium form-input">{initial.othername || "N/A"}</p>
                      </div>
                      <div className="form-label">
                        <p className="form-label">Lastname</p>
                        <p className="font-medium form-input">{initial.lastname}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="my-3">
                    <label className="form-label mb-2">
                      First name
                      <Field
                        name="firstname"
                        className="form-input mt-1 w-full"
                        placeholder="Employee firstname"
                      />
                      <ErrorMessage name="firstname" component="div" className="text-xs text-red-500 mt-1" />
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="form-label">
                        Othername (optional)
                        <Field
                          name="othername"
                          className="form-input mt-1 w-full"
                          placeholder="Employee othername"
                        />
                      </label>

                      <label className="form-label">
                        Last name
                        <Field
                          name="lastname"
                          className="form-input mt-1 w-full"
                          placeholder="Employee lastname"
                        />
                      </label>
                    </div>
                  </div>
                )}

                {/* CONTACT INFORMATION */}
                {initial ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="form-label">
                        <p className="form-label">Email</p>
                        <p className="font-medium form-input">{initial.email}</p>
                      </div>
                      <div className="form-label">
                        <p className="form-label">Phone</p>
                        <p className="font-medium form-input">{initial.phoneNumber}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="form-label">
                        Email
                        <Field
                          name="email"
                          className="form-input mt-1 w-full"
                          placeholder="Employee email"
                        />
                        <ErrorMessage name="email" component="div" className="text-xs text-red-500 mt-1" />
                      </label>

                      <label className="form-label">
                        Phone
                        <Field
                          name="phoneNumber"
                          className="form-input mt-1 w-full"
                          placeholder="Employee phone number"
                        />
                      </label>
                    </div>
                  </>
                )}


                {/* ROLE & STORE INFORMATION */}
                {initial ? (
                  <>
                    <div className="flex items-center justify-between mt-4">
                      <h4 className="font-semibold text-gray-700">Roles and Stores</h4>
                      <button
                        type="button"
                        className="modal-btn-primary"
                        onClick={() => {
                          setOpenEditRoleInfoModal(true)
                        }}
                      >
                        <EditIcon />
                        Edit
                      </button>
                    </div>
                    <div className="space-y-2 grid grid-cols-2 my-3">
                      <div className="text-left text-lg">
                        <p className="form-label">Main Role: </p>
                        <p className="font-bold">
                          {initial.assignedRoles ? (initial.assignedRoles[0].name) : ("N/A")}
                        </p>
                      </div>

                      <div className="text-left">
                        <p className="form-label">Other Roles: </p>
                        {(initial.assignedRoles ?? [])
                          .slice(1) // ✅ start from the 2nd element (index 1)
                          .map((r) => (
                            <span
                              key={r.id}
                              className="md:text-lg sm:text-sm me-2 bg-gray-100 px-4 py-2 rounded-full"
                            >
                              {r.name}
                            </span>
                          ))}
                      </div>
                    </div>

                    <div className="space-y-1 text-left">
                      <p className="form-label">Assigned Stores</p>
                      {initial.assignedStores?.map((store) => {
                        const actualStore = stores.find((s) => s.id === store.id);
                        return (
                          <span key={actualStore?.id} className="tile">
                            {actualStore?.name}
                          </span>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-3">
                      {/* --- MAIN ROLE (Single Select) --- */}
                      <label className="form-label">
                        Main Role
                        <Field
                          as="select"
                          name="mainRole"
                          value={values.mainRole ?? ""} // supports null
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            const newMain = e.target.value || null;

                            // auto-remove main role from other roles if it exists there
                            const updatedOtherRoles = values.roleIds && values?.roleIds.filter((id) => id !== newMain);

                            setFieldValue("mainRole", newMain);
                            setFieldValue("roleIds", updatedOtherRoles);
                          }}
                          className="form-input mt-1 w-full"
                        >
                          <option value="">Select Main Role</option>
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </Field>

                        {errors.mainRole && touched.mainRole && (
                          <div className="text-xs text-red-500 mt-1">{errors.mainRole as string}</div>
                        )}
                      </label>

                      {/* --- OTHER ROLES (Multi-select Button Chips) --- */}
                      <label className="form-label">
                        Other Roles
                        <div className="flex flex-wrap gap-2 mt-1">
                          {roles.map((r) => {
                            const selected = values.roleIds?.includes(r.id);
                            const isMain = values.mainRole === r.id;

                            return (
                              <button
                                type="button"
                                key={r.id}
                                disabled={isMain}
                                onClick={() => {
                                  if (selected) {
                                    setFieldValue("roleIds", values.roleIds?.filter((id) => id !== r.id));
                                  } else if (!isMain) {
                                    setFieldValue("roleIds", [...(values.roleIds ?? []), r.id]);
                                  }
                                }}
                                className={`px-3 py-1 rounded-full border text-sm transition ${selected
                                    ? "bg-primary text-white border-primary"
                                    : "bg-gray-100 text-gray-700 border-gray-300"
                                  } ${isMain ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                {r.name}
                              </button>
                            );
                          })}
                        </div>

                        {errors.roleIds && touched.roleIds && (
                          <div className="text-xs text-red-500 mt-1">{errors.roleIds as string}</div>
                        )}
                      </label>

                    </div>
                    {/* --- STORES (Multi-select Button Chips) --- */}
                    <label className="form-label">
                      Stores
                      <div className="flex flex-row flex-wrap gap-2 mt-1">
                        {stores.map((s) => {
                          const selected = values.stores?.includes(s.id);

                          return (
                            <button
                              type="button"
                              key={s.id}
                              onClick={() => {
                                if (selected) {
                                  setFieldValue("stores", values.stores?.filter((id) => id !== s.id));
                                } else {
                                  setFieldValue("stores", [...(values.stores ?? []), s.id]);
                                }
                              }}
                              className={`px-3 py-1 rounded-full border text-sm text-nowrap transition ${selected
                                  ? "bg-primary text-white border-primary"
                                  : "bg-gray-100 text-gray-700 border-gray-300"
                                } cursor-pointer`}
                            >
                              {s.name} — {s.location}
                            </button>
                          );
                        })}
                      </div>

                      {errors.stores && touched.stores && (
                        <div className="text-xs text-red-500 mt-1">
                          {(errors.stores as any) || "Assign at least one store"}
                        </div>
                      )}
                    </label>
                  </>
                )}

                {/* BUTTONS */}
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-md border"
                  >
                    Cancel
                  </button>
                  {!initial && (
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-md bg-primary text-white"
                      disabled={adding}
                    >
                      {adding ?
                        <div className="flex items-center gap-2">
                          <span className="loader"></span>
                          <span>Saving...</span>
                        </div>
                        : "Save Employee"
                      }
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <UpdateEmployeeBasicInfoModal
        isOpen={openEditBasicInfoModal}
        employee={initial as EmployeeItem}
        onClose={() => {
          onClose()
          setOpenEditBasicInfoModal(false)
        }}
      />

      <UpdateRolesAndStoresModal
        isOpen={openEditRoleInfoModal}
        employee={initial as EmployeeItem}
        onClose={() => {
          onClose()
          setOpenEditRoleInfoModal(false)
        }}
        roles={roles}
        stores={stores as Store[]}
      />
    </>
  );
};

export default AddEditEmployeeModal;
